import React, { useState, useEffect, useContext } from 'react';
import { Text, View, Pressable, StyleSheet } from "react-native";
import { useNavigate } from 'react-router-dom';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toast } from 'react-toastify';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import AddCoachModal from '../../../account_screen/components/AddCoachModal';
import CampCoachCard from './CampCoachCard';
import DataOps from '../../../../data/DataOps';
import NotificationOps from '../../../../data/NotificationOps';

const CampPageHostList = ({ camp, setCamp, isEditing = false, hideHeader = false, isPeronalInfoPage = false}) => {
    const navigate = useNavigate();

    const [coaches, setCoaches] = useState<string[]>(camp.campCoaches || []);
    const [coachData, setCoachData] = useState<any[]>([]);
    const [showCoachSearch, setShowCoachSearch] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const cardsPerRow = isPeronalInfoPage ? 1 : 3;
    const cardWidth = isPeronalInfoPage ? 75 : 100;

    const canEdit = isEditing;

    useEffect(() => {
        setCoaches(camp.campCoaches || []);
    }, [camp.campCoaches]);

    useEffect(() => {
        const fetchCoachData = async () => {
            if (coaches.length === 0) {
                setCoachData([]);
                return;
            }
            const list = await DataOps.fetchCoachDataList(coaches);
            setCoachData(list);
        };

        fetchCoachData();
    }, [coaches]);

    const handleAddCoach = async (coach: any) => {
        const success = await DataOps.linkCoachToCamp(camp.campId, coach.userId);

        if (success) {
            const newCoaches = [...coaches, coach.userId];

            const updatedCamp = {
                ...camp,
                campCoaches: newCoaches,
            };
            setCamp(updatedCamp);
            setCoaches(newCoaches);

            const updatedCoachList = await DataOps.fetchCoachDataList(newCoaches);
            setCoachData(updatedCoachList);

            try {
                await NotificationOps.createNotificationForAnyAccount(
                    coach.userId,
                    `You have been added as a coach to ${camp.campName || 'a camp'}.`,
                    'coach_added',
                    undefined,
                    camp.campId,
                    { campName: camp.campName }
                );
            } catch (notifErr) {
                console.error('Error sending notification to coach:', notifErr);
            }

            toast.success(`Coach ${coach.firstName} ${coach.lastName} added successfully!`, {
                position: "bottom-center",
                autoClose: 3000,
            });
        } else {
            toast.error('Failed to add coach. Please try again.', {
                position: "bottom-center",
                autoClose: 3000,
            });
        }
    };

    const handleRemoveCoach = async (coachId: string) => {
        const success = await DataOps.unlinkCoachFromCamp(camp.campId, coachId);

        if (success) {
            const newCoaches = coaches.filter(id => id !== coachId);

            const updatedCamp = {
                ...camp,
                campCoaches: newCoaches,
            };
            setCamp(updatedCamp);

            const updatedCoachList = coachData.filter(c => (c.userId || c.id) !== coachId);
            setCoachData(updatedCoachList);
            setCoaches(newCoaches);

            try {
                await NotificationOps.createNotificationForAnyAccount(
                    coachId,
                    `You have been removed as a coach from ${camp.campName || 'a camp'}.`,
                    'coach_removed',
                    undefined,
                    camp.campId,
                    { campName: camp.campName }
                );
            } catch (notifErr) {
                console.error('Error sending notification to coach:', notifErr);
            }

            toast.success('Coach removed successfully!', {
                position: "bottom-center",
                autoClose: 3000,
            });
        } else {
            toast.error('Failed to remove coach. Please try again.', {
                position: "bottom-center",
                autoClose: 3000,
            });
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: any, index: number) => {
        e.preventDefault();
        if (draggedIndex === null) return;
        setDragOverIndex(index);
    };

    const handleDrop = async (e: any, dropIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newCoaches = [...coaches];
        const [draggedCoach] = newCoaches.splice(draggedIndex, 1);
        newCoaches.splice(dropIndex, 0, draggedCoach);

        setCoaches(newCoaches);
        setDraggedIndex(null);
        setDragOverIndex(null);

        const updatedCamp = {
            ...camp,
            campCoaches: newCoaches,
        };
        setCamp(updatedCamp);
        await DataOps.saveCustomDataToFirebase('camp', camp.campId, updatedCamp, true);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };


    const styles = StyleSheet.create({
        grid: {
            display: 'grid',
            alignItems: 'center',
            width: `${cardWidth}%` as any,
            gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
            gap: 16,
        } as any,
        coachCardWrapper: isPeronalInfoPage ? {
            width: '100%',
            maxWidth: 600,
            marginBottom: 16,
            borderRadius: 12,
        } : {
            flexDirection: 'row',
            alignItems: 'stretch',
            marginBottom: 12,
            borderRadius: 12,
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            cursor: 'grab',
        } as any,
        dragging: {
            opacity: 0.5,
            cursor: 'grabbing',
        } as any,
        dragOver: {
            borderTopWidth: 3,
            borderTopColor: '#2196F3',
            marginTop: 8,
        },
        dragHandle: {
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: 8,
            backgroundColor: '#F8F8F8',
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            cursor: 'grab',
        } as any,
        coachCardContent: {
            flex: 1,
        },
    });

    return (
        <View style={{ flexDirection: 'column', width: '100%', marginTop: 20, alignItems: isPeronalInfoPage ? 'center' : 'flex-start' }}>
            {!hideHeader && (
                <View style={{ flexDirection: 'row', marginBottom: 40, alignItems: 'flex-end' }}>
                    <Text style={{ fontSize: 20, fontWeight: '500', marginRight: 30 }}>Camp Coaches</Text>

                    {canEdit && (
                        <>
                            <MaterialCommunityIcons name={'plus-circle-outline'} size={15} color='#34C759' />
                            <Pressable onPress={() => setShowCoachSearch(true)}>
                                <Text style={{ fontSize: 15, fontWeight: '500', color: '#34C759', marginLeft: 3 }}>
                                    add coach
                                </Text>
                            </Pressable>
                        </>
                    )}
                </View>
            )}

            {coachData.length > 0 ? (
                <View style={styles.grid}>
                    {coachData.map((coach, index) => {
                        if (isPeronalInfoPage) {
                            // Personal info page: simple centered layout, no drag handles
                            return (
                                <View key={coach.userId || coach.id} style={styles.coachCardWrapper}>
                                    <Pressable
                                        onPress={() => navigate(`/coach/${coach.userId || coach.id}`)}
                                    >
                                        <CampCoachCard
                                            coachId={coach.userId || coach.id}
                                            campId={camp.campId}
                                            isEditing={canEdit}
                                            onRemove={() => handleRemoveCoach(coach.userId || coach.id)}
                                        />
                                    </Pressable>
                                </View>
                            );
                        }

                        // Regular camp page: grid with drag handles
                        const wrapperStyle = [
                            styles.coachCardWrapper,
                            draggedIndex === index && styles.dragging,
                            dragOverIndex === index && draggedIndex !== index && styles.dragOver,
                        ];

                        const DraggableWrapper = 'div' as any;

                        return (
                            <DraggableWrapper
                                key={coach.userId || coach.id}
                                draggable={canEdit}
                                onDragStart={() => canEdit && handleDragStart(index)}
                                onDragOver={(e: any) => canEdit && handleDragOver(e, index)}
                                onDrop={(e: any) => canEdit && handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                style={StyleSheet.flatten(wrapperStyle)}
                            >
                                {canEdit && (
                                    <View style={styles.dragHandle}>
                                        <MaterialCommunityIcons name="drag-vertical" size={24} color="#999" />
                                    </View>
                                )}
                                <Pressable
                                    onPress={() => navigate(`/coach/${coach.userId || coach.id}`)}
                                    style={styles.coachCardContent}
                                >
                                    <CampCoachCard
                                        coachId={coach.userId || coach.id}
                                        campId={camp.campId}
                                        isEditing={canEdit}
                                        onRemove={() => handleRemoveCoach(coach.userId || coach.id)}
                                    />
                                </Pressable>
                            </DraggableWrapper>
                        );
                    })}
                </View>
            ) : (
                <View style={{
                    padding: 32,
                    alignItems: 'center',
                    backgroundColor: '#F8F8F8',
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: '#E0E0E0',
                    borderStyle: 'dashed'
                }}>
                    <MaterialCommunityIcons name="whistle-outline" size={48} color="#ccc" />
                    <Text style={{ fontSize: 16, color: '#666', marginTop: 12, textAlign: 'center' }}>
                        No coaches added yet
                    </Text>
                    {canEdit && (
                        <Text style={{ fontSize: 14, color: '#999', marginTop: 4, textAlign: 'center' }}>
                            Click "add coach" to search for registered coaches
                        </Text>
                    )}
                </View>
            )}

            {/* Coach Search Modal */}
            <AddCoachModal
                visible={showCoachSearch}
                onClose={() => setShowCoachSearch(false)}
                onAddCoach={handleAddCoach}
            />
        </View>
    );
};

export default CampPageHostList;
